
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  DoorOpen,
  Key,
  QrCode,
  AlertCircle,
  HelpCircle
} from "lucide-react";
import { format } from "date-fns";

interface BookingAccessDetailsProps {
  hasEnabledDevices: boolean;
  isCheckedIn: boolean;
  isCompleted: boolean;
  isCancelled: boolean;
  currentAccessCode: any;
  createAccessCodePending: boolean;
  qrCodeDataUrl: string;
}

export const BookingAccessDetails = ({
  hasEnabledDevices,
  isCheckedIn,
  isCompleted,
  isCancelled,
  currentAccessCode,
  createAccessCodePending,
  qrCodeDataUrl
}: BookingAccessDetailsProps) => {
  const [showInstructions, setShowInstructions] = useState(false);

  return (
    <Card id="access-details-section" className="border shadow-sm bg-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <DoorOpen className="h-5 w-5 mr-2 text-muted-foreground" />
          Access Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Entrance Code - Only show if there are enabled devices */}
        {hasEnabledDevices && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center">
              <Key className="h-4 w-4 mr-2" />
              Entrance Code
            </h4>
            {/* REVERTED: Only show access code after check-in */}
            {isCheckedIn && currentAccessCode && !isCompleted && !isCancelled ? (
              <div className="space-y-3">
                <div className="bg-muted p-4 rounded-lg border-2 border-dashed border-border">
                  <p className="text-2xl font-mono font-bold text-center text-foreground tracking-wider">
                    {currentAccessCode.code}
                  </p>
                  <p className="text-xs text-center text-muted-foreground mt-2">
                    Generated: {format(new Date(currentAccessCode.created_at), 'MMM d, h:mm a')}
                  </p>
                </div>
                
                <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                    >
                      <HelpCircle className="h-4 w-4 mr-2" />
                      How to use the code
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center">
                        <Key className="h-5 w-5 mr-2" />
                        How to Use Your Entrance Code
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-muted text-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                            1
                          </span>
                          <p className="text-sm text-muted-foreground">
                            At the start of your session time, touch the keypad on the door for the digits to light up.
                          </p>
                        </div>
                        <div className="flex items-start space-x-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-muted text-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                            2
                          </span>
                          <p className="text-sm text-muted-foreground">
                            Enter the 4 digit code and press the unlock icon.
                          </p>
                        </div>
                        <div className="flex items-start space-x-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-muted text-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                            3
                          </span>
                          <p className="text-sm text-muted-foreground">
                            The door will unlock and you can enter the room.
                          </p>
                        </div>
                        <div className="flex items-start space-x-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-destructive/10 text-destructive rounded-full flex items-center justify-center text-sm font-semibold">
                            4
                          </span>
                          <p className="text-sm text-muted-foreground">
                            At the end of the session, ensure you return to your booking page and click check out. <span className="font-semibold text-destructive">Failing to check out can result in overstay charges.</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            ) : (
              <div className="bg-muted p-4 rounded-lg text-center">
                <Key className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {createAccessCodePending ? "Generating code..." : 
                   isCompleted ? "Session completed" :
                   isCancelled ? "Booking cancelled" :
                   "Available after check-in"}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Show message when no enabled devices */}
        {!hasEnabledDevices && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center">
              <Key className="h-4 w-4 mr-2" />
              Access Information
            </h4>
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg text-center">
              <AlertCircle className="h-6 w-6 mx-auto mb-2 text-amber-600" />
              <p className="text-sm text-amber-700 font-medium mb-1">
                Manual Entry Required
              </p>
              <p className="text-xs text-amber-600">
                Self-service access is currently unavailable. Please show the QR code below to a staff member to assist you with check in
              </p>
            </div>
          </div>
        )}

        {/* QR Code - Now shows immediately for confirmed bookings */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center">
            <QrCode className="h-4 w-4 mr-2" />
            Booking QR Code
          </h4>
          <p className="text-xs text-muted-foreground mb-3">
            {hasEnabledDevices 
              ? "Show this QR code to staff for booking verification and assistance"
              : "Show this QR code to staff for entry assistance and booking verification"
            }
          </p>
          <div className="bg-background p-4 rounded-lg border border-border">
            {qrCodeDataUrl && !isCancelled ? (
              <div className="w-full aspect-square flex items-center justify-center">
                <img 
                  src={qrCodeDataUrl} 
                  alt="Booking Management QR Code" 
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-full aspect-square bg-muted rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <QrCode className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    {isCancelled ? "Booking cancelled" : "QR code unavailable"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
